import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Artikel, CreateVertragsartikelRequest } from '../types';
import artikelService from '../services/artikelService';
import { Button, inputClass } from './ui';
import { formatPreis } from '../utils/preis';

/**
 * Die automatische Pachtverlängerung eines neuen Vertrags. Die Grundpacht für die Laufzeit steht als
 * einmalige Leistung in der Positionsliste; ist die Verlängerung angehakt, wird danach jedes Jahr ein
 * weiteres Pachtjahr berechnet, bis der Vertrag beendet wird.
 *
 * Hinterlegt wird nur der Artikel: berechnet wird immer mit seinem aktuellen Preis aus der
 * Preisliste, damit eine Preisanpassung für alle laufenden Verträge gilt.
 */
export interface VerlaengerungEntwurf {
  aktiv: boolean;
  artikel: Artikel | null;
}

/** Neue Verträge verlängern sich standardmäßig automatisch; der Artikel wird beim Laden vorgeschlagen. */
export const standardVerlaengerung: VerlaengerungEntwurf = { aktiv: true, artikel: null };

/** Prüfen und in eine Position übersetzen; null = keine Verlängerung, String = Fehlermeldung. */
export const verlaengerungZuRequest = (v: VerlaengerungEntwurf): CreateVertragsartikelRequest | null | string => {
  if (!v.aktiv) return null;
  if (!v.artikel) return 'Automatische Verlängerung: bitte den Pachtartikel wählen';
  return { artikelId: v.artikel.id, abrechnungsart: 'VERLAENGERUNG', menge: 1 };
};

/** ISO-Datum + n Jahre als dd.mm.yyyy (der 29.02. rutscht auf den 28.02.). */
const plusJahre = (iso: string, jahre: number): string => {
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  const zielJahr = y + jahre;
  const tag = Math.min(d, new Date(Date.UTC(zielJahr, m, 0)).getUTCDate());
  return `${String(tag).padStart(2, '0')}.${String(m).padStart(2, '0')}.${zielJahr}`;
};

const VerlaengerungErfassung: React.FC<{
  wert: VerlaengerungEntwurf;
  onChange: (wert: VerlaengerungEntwurf) => void;
  unterschriftsdatum?: string;
  laufzeitJahre?: number;
}> = ({ wert, onChange, unterschriftsdatum, laufzeitJahre }) => {
  const [artikel, setArtikel] = useState<Artikel[]>([]);
  const [suche, setSuche] = useState('');

  useEffect(() => {
    artikelService.getAll().then(setArtikel).catch(() => setArtikel([]));
  }, []);

  const vorschlagen = (liste: Artikel[]) => liste.find(a => a.active && /pachtverl/i.test(a.bezeichnung)) ?? null;

  // Standardmäßig angehakt: sobald die Preisliste da ist, einmalig den Jahresartikel vorschlagen.
  // Nur einmal – sonst würde „Ändern“ (Artikel leeren) sofort wieder überschrieben.
  const vorgeschlagen = useRef(false);
  useEffect(() => {
    if (vorgeschlagen.current || artikel.length === 0) return;
    vorgeschlagen.current = true;
    if (wert.aktiv && !wert.artikel) onChange({ ...wert, artikel: vorschlagen(artikel) });
  }, [artikel, wert, onChange]);

  const treffer = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return [];
    return artikel
      .filter(a => a.active && `${a.artikelnummer} ${a.bezeichnung}`.toLowerCase().includes(q))
      .slice(0, 25);
  }, [artikel, suche]);

  const waehle = (a: Artikel) => {
    onChange({ ...wert, artikel: a });
    setSuche('');
  };

  /**
   * Beim Anhaken den Jahresartikel der Preisliste vorschlagen („Pachtverlängerung 1 Jahr“). Bewusst
   * nicht die Grundpacht: die ist ein Gesamtpreis für mehrere Jahre und wäre als Jahrespreis falsch.
   */
  const umschalten = (aktiv: boolean) => {
    if (!aktiv || wert.artikel) { onChange({ ...wert, aktiv }); return; }
    onChange({ aktiv, artikel: vorschlagen(artikel) });
  };

  const beginn = unterschriftsdatum && laufzeitJahre ? plusJahre(unterschriftsdatum, laufzeitJahre) : null;

  return (
    <div>
      <label className="-mx-2 flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg px-2 py-1.5 active:bg-gray-50">
        <input
          type="checkbox"
          checked={wert.aktiv}
          onChange={e => umschalten(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">Nach der Laufzeit automatisch verlängern</span>
          <span className="block text-xs text-gray-500">
            {wert.aktiv
              ? `${beginn ? `Ab ${beginn} jedes` : 'Jedes'} Jahr ein weiteres Pachtjahr, bis der Vertrag beendet wird.`
              : 'Der Vertrag endet mit der Laufzeit, danach wird nichts mehr berechnet.'}
          </span>
        </span>
      </label>

      {wert.aktiv && (
        <div className="mt-2 space-y-1.5 pl-8">
          {wert.artikel ? (
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900">{wert.artikel.bezeichnung}</p>
                <p className="text-xs text-gray-500">
                  {wert.artikel.artikelnummer} · derzeit {formatPreis(wert.artikel.preis)} netto pro Jahr
                </p>
              </div>
              <Button type="button" variant="secondary" onClick={() => onChange({ ...wert, artikel: null })}>Ändern</Button>
            </div>
          ) : (
            <div>
              <input
                autoFocus
                placeholder="Pachtartikel suchen (Preis pro Jahr)…"
                autoComplete="off"
                value={suche}
                onChange={e => setSuche(e.target.value)}
                onKeyDown={e => {
                  // Enter übernimmt den ersten Treffer statt das Formular abzuschicken.
                  if (e.key === 'Enter') { e.preventDefault(); if (treffer[0]) waehle(treffer[0]); }
                }}
                className={`${inputClass} min-h-[44px]`}
              />
              {treffer.length > 0 && (
                <ul className="mt-2 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                  {treffer.map(a => (
                    <li key={a.id}>
                      <button type="button" onClick={() => waehle(a)} className="flex min-h-[44px] w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-brand-50 active:bg-brand-100">
                        <span className="whitespace-nowrap text-xs tabular-nums text-gray-400">{a.artikelnummer}</span>
                        <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{a.bezeichnung}</span>
                        <span className="whitespace-nowrap text-xs tabular-nums text-gray-500">{formatPreis(a.preis)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <p className="text-xs text-gray-500">Berechnet wird immer der aktuelle Preis aus der Preisliste.</p>
        </div>
      )}
    </div>
  );
};

export default VerlaengerungErfassung;
